import { DateTimePicker, type DateTimePickerProps } from '@mantine/dates';
import { IconCalendarTime } from '@tabler/icons-react';
import { formatDay, fromDateTimeValue, toDateTimeValue } from '../format.js';
import { useDatesLabels } from './dates.js';

export interface DateTimeInputProps
  extends Omit<
    DateTimePickerProps,
    | 'value'
    | 'defaultValue'
    | 'onChange'
    | 'valueFormat'
    | 'withSeconds'
    | 'type'
    | 'minDate'
    | 'maxDate'
    | 'ref'
  > {
  /** Yerel tarih-saat `YYYY-MM-DDTHH:mm` (`datetime-local` ile aynı); boşsa `''`. */
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** En erken ve en geç an, aynı biçimde. */
  minDate?: string | undefined;
  maxDate?: string | undefined;
}

/** Mantine değeri (`YYYY-MM-DD HH:mm:ss`) → görünüm `GG.AA.YYYY SS:DD`. */
function display(value: string): string {
  const local = fromDateTimeValue(value);
  return local ? `${formatDay(local.slice(0, 10))} ${local.slice(11, 16)}` : '';
}

/**
 * Tarih ve saat girişi (M6f G1-04; ADR 0033): görünüm `GG.AA.YYYY SS:DD`; takvimden gün, ayrı
 * alanlarda 24 saatlik saat. İşletim sisteminin dil biçiminden bağımsızdır (yerel
 * `<input type="datetime-local">` yerine). Değer yereldir; UTC'ye çevirmek ekranın işidir (gemi saati
 * farkıyla).
 */
export function DateTimeInput({
  value,
  onChange,
  minDate,
  maxDate,
  leftSection,
  rightSection,
  readOnly,
  placeholder,
  timePickerProps,
  submitButtonProps,
  ...rest
}: DateTimeInputProps) {
  const labels = useDatesLabels();
  const icon =
    leftSection || rightSection !== undefined || readOnly ? (
      rightSection
    ) : (
      <IconCalendarTime size={16} aria-hidden />
    );
  const min = toDateTimeValue(minDate);
  const max = toDateTimeValue(maxDate);
  return (
    <DateTimePicker
      {...rest}
      value={toDateTimeValue(value)}
      onChange={(next) => onChange(fromDateTimeValue(next))}
      valueFormat={display}
      withSeconds={false}
      locale={labels.locale}
      previousLabel={labels.previousLabel}
      nextLabel={labels.nextLabel}
      placeholder={placeholder ?? labels.dateTimePlaceholder}
      readOnly={readOnly ?? false}
      leftSection={leftSection}
      rightSection={icon}
      {...(rest.clearable ? {} : { rightSectionPointerEvents: 'none' as const })}
      timePickerProps={{
        format: '24h',
        hoursInputLabel: labels.hoursInputLabel,
        minutesInputLabel: labels.minutesInputLabel,
        ...timePickerProps,
      }}
      submitButtonProps={{ 'aria-label': labels.apply, ...submitButtonProps }}
      {...(min ? { minDate: min } : {})}
      {...(max ? { maxDate: max } : {})}
    />
  );
}
